import { PdfFont, PdfSize, PageGen, PdfBrush, PdfColor, PdfRect, ETextAlign } from '../pdf';
import { InlineElement, LatexInline, FootnoteInline, EndnoteInline, CitationInline } from './types';
import { getHyphenator } from './hyphenator';
import { moduleRegistry } from '../../modules/registry';

export interface TextItem {
  type: 'box' | 'glue' | 'penalty';
  width: number;
  stretch?: number;
  shrink?: number;
  penalty?: number;
  flagged?: boolean;
  content?: string;
  font?: PdfFont;
  fontSize?: number;
  brush?: PdfBrush;
  isLatex?: boolean;
  isModule?: boolean;
  moduleName?: string;
  moduleContent?: any;
  isNote?: boolean;
  noteType?: 'footnote' | 'endnote' | 'citation';
  noteContent?: string;
  noteId?: string;
  noteNumber?: number;
}

export interface BreakPoint {
  position: number;
  line: number;
  fitness: number;
  totalDemerits: number;
  previous: BreakPoint | null;
  ratio: number;
}

export interface LineBreakResult {
  breaks: BreakPoint[];
  items: TextItem[];
}

export interface LayoutParams {
  font: PdfFont;
  page?: PageGen;
  lineWidth: number;
  tolerance: number;
}

const INFINITE_PENALTY = 1e15;
const DEMERIT_PENALTY = 10;
const DEMERIT_FLAGGED = 100;
const DEMERIT_FITNESS = 100;

export function inlinesToItems(
  inlines: InlineElement[],
  baseFont: PdfFont,
  boldFont: PdfFont,
  italicFont: PdfFont,
  monoFont: PdfFont,
  page: PageGen,
  language = 'en'
): TextItem[] {
  const items: TextItem[] = [];
  const hyphenator = getHyphenator(language);
  const hyphenWidth = page.pdf.getFont(baseFont.font).widthOfTextAtSize('-', baseFont.size);

  const processInline = (
    inline: InlineElement,
    getFont: (base: PdfFont) => PdfFont,
    getBrush: () => PdfBrush
  ): void => {
    switch (inline.type) {
      case 'text': {
        let {content} = inline;
        let hasTrailingSpace = false;

        // Handle leading whitespace
        const leadingMatch = /^\s+/.exec(content);
        if (leadingMatch) {
          const spaceWidth = page.pdf.getFont(baseFont.font).widthOfTextAtSize(' ', baseFont.size);
          const spaceStretch = spaceWidth * 0.5;
          const spaceShrink = spaceWidth * 0.3;

          items.push({
            type: 'glue',
            width: spaceWidth,
            stretch: spaceStretch,
            shrink: spaceShrink,
          });
          content = content.slice(leadingMatch[0].length);
        }

        // Check for trailing whitespace (we'll handle it after processing words)
        const trailingMatch = /\s+$/.exec(content);
        if (trailingMatch) {
          hasTrailingSpace = true;
          content = content.slice(0, -trailingMatch[0].length);
        }

        const words = content.split(/\s+/);
        for (let i = 0; i < words.length; i++) {
          const word = words[i];
          if (word.length === 0) continue;

          const wordFont = getFont(baseFont);

          // Check if word is long enough for hyphenation
          if (word.length > 6) {
            const parts = hyphenator.hyphenate(word);

            if (parts.length > 1) {
              // Add word parts with hyphenation points
              for (let j = 0; j < parts.length; j++) {
                const part = parts[j];
                const partWidth = page.pdf
                  .getFont(wordFont.font)
                  .widthOfTextAtSize(part, wordFont.size);

                // Add the word part as a box
                items.push({
                  type: 'box',
                  width: partWidth,
                  content: part,
                  font: wordFont,
                  brush: getBrush(),
                });

                // If not the last part, add a hyphenation penalty
                if (j < parts.length - 1) {
                  items.push({
                    type: 'penalty',
                    width: hyphenWidth,
                    penalty: 50, // Hyphenation penalty
                    flagged: true,
                    content: '-',
                    font: wordFont,
                    brush: getBrush(),
                  });
                }
              }
            } else {
              // Word doesn't have hyphenation points, add as single box
              const wordWidth = page.pdf
                .getFont(wordFont.font)
                .widthOfTextAtSize(word, wordFont.size);
              items.push({
                type: 'box',
                width: wordWidth,
                content: word,
                font: wordFont,
                brush: getBrush(),
              });
            }
          } else {
            // Short word, no hyphenation
            const wordWidth = page.pdf
              .getFont(wordFont.font)
              .widthOfTextAtSize(word, wordFont.size);
            items.push({
              type: 'box',
              width: wordWidth,
              content: word,
              font: wordFont,
              brush: getBrush(),
            });
          }

          // Add glue (space) after the word, unless it's the last word
          if (i < words.length - 1) {
            const spaceWidth = page.pdf
              .getFont(baseFont.font)
              .widthOfTextAtSize(' ', baseFont.size);
            const spaceStretch = spaceWidth * 0.5;
            const spaceShrink = spaceWidth * 0.3;

            // Add penalty for breaking after short words (1-2 characters)
            if (word.length <= 2) {
              items.push({
                type: 'penalty',
                width: 0,
                penalty: 5000, // Very high penalty to discourge breaking after short words
                flagged: false,
              });
            }

            items.push({
              type: 'glue',
              width: spaceWidth,
              stretch: spaceStretch,
              shrink: spaceShrink,
            });
          }
        }

        // Add glue for trailing whitespace
        if (hasTrailingSpace) {
          const spaceWidth = page.pdf.getFont(baseFont.font).widthOfTextAtSize(' ', baseFont.size);
          const spaceStretch = spaceWidth * 0.5;
          const spaceShrink = spaceWidth * 0.3;

          items.push({
            type: 'glue',
            width: spaceWidth,
            stretch: spaceStretch,
            shrink: spaceShrink,
          });
        }
        break;
      }

      case 'bold':
      case 'italic': {
        const childFont = inline.type === 'bold' ? boldFont : italicFont;
        for (const child of inline.children) {
          processInline(child, () => childFont, getBrush);
        }
        break;
      }

      case 'code': {
        const wordFont = monoFont;
        const wordWidth = page.pdf
          .getFont(wordFont.font)
          .widthOfTextAtSize(inline.content, wordFont.size);

        items.push({
          type: 'box',
          width: wordWidth,
          content: inline.content,
          font: wordFont,
          brush: PdfBrush.Black,
        });

        const spaceWidth = page.pdf.getFont(baseFont.font).widthOfTextAtSize(' ', baseFont.size);
        items.push({
          type: 'glue',
          width: spaceWidth,
          stretch: spaceWidth * 0.5,
          shrink: spaceWidth * 0.3,
        });
        break;
      }

      case 'link': {
        const linkBrush = new PdfBrush(PdfColor.fromRgb(0, 0, 255));
        for (const child of inline.children) {
          processInline(
            child,
            (f) => f,
            () => linkBrush
          );
        }
        break;
      }

      case 'latex': {
        const latexNode = inline;
        const isDisplay = latexNode.display === true;
        const latexContent = inline.content;

        const fontSize = isDisplay ? baseFont.size * 1.2 : baseFont.size;
        const latexSize = page.measureLatex(latexContent, fontSize);

        items.push({
          type: 'box',
          width: latexSize.width,
          content: latexContent,
          font: baseFont,
          fontSize,
          brush: getBrush(),
          isLatex: true,
        });

        if (!isDisplay) {
          const spaceWidth = page.pdf.getFont(baseFont.font).widthOfTextAtSize(' ', baseFont.size);
          items.push({
            type: 'glue',
            width: spaceWidth,
            stretch: spaceWidth * 0.5,
            shrink: spaceWidth * 0.3,
          });
        }
        break;
      }

      case 'module': {
        const mod = moduleRegistry.get(inline.moduleName);
        if (mod) {
          const moduleContent = mod.parse(inline.content, inline.attrs);
          const modSize = mod.measure(moduleContent, { font: baseFont, brush: getBrush(), page });

          items.push({
            type: 'box',
            width: modSize.width,
            content: '',
            moduleName: inline.moduleName,
            moduleContent,
            font: baseFont,
            brush: getBrush(),
            isModule: true,
          });
        } else {
          const moduleText = `<${inline.moduleName}>${inline.content}</${inline.moduleName}>`;
          const wordWidth = page.pdf
            .getFont(baseFont.font)
            .widthOfTextAtSize(moduleText, baseFont.size);
          items.push({
            type: 'box',
            width: wordWidth,
            content: moduleText,
            font: baseFont,
            brush: getBrush(),
          });
        }

        const spaceWidth = page.pdf.getFont(baseFont.font).widthOfTextAtSize(' ', baseFont.size);
        items.push({
          type: 'glue',
          width: spaceWidth,
          stretch: spaceWidth * 0.5,
          shrink: spaceWidth * 0.3,
        });
        break;
      }

      case 'footnote':
      case 'endnote':
      case 'citation': {
        const noteInline = inline;
        const number = noteInline.number || 0;
        const markerText = String(number);
        const markerFontSize = baseFont.size * 0.7;
        const markerFont = new PdfFont(baseFont.font, markerFontSize);
        const markerWidth = page.pdf
          .getFont(baseFont.font)
          .widthOfTextAtSize(markerText, markerFontSize);
        items.push({
          type: 'box',
          width: markerWidth,
          content: markerText,
          font: markerFont,
          brush: getBrush(),
          isNote: true,
          noteType: inline.type,
          noteContent: (noteInline as any).content as string,
          noteId: inline.type === 'citation' ? (inline).id : undefined,
          noteNumber: number,
        });
        break;
      }
    }
  };

  for (const inline of inlines) {
    processInline(
      inline,
      (f) => f,
      () => PdfBrush.Black
    );
  }

  // Forced break at the end of paragraph
  items.push({
    type: 'penalty',
    width: 0,
    penalty: 1000000,
    flagged: false,
  });

  return items;
}

export function breakLinesKnuth(items: TextItem[], lineWidth: number): BreakPoint[] {
  const breaks: BreakPoint[] = [];
  const n = items.length;
  if (n === 0) return [];

  const dp: BreakPoint[] = new Array(n + 1);

  dp[0] = {
    position: 0,
    line: 0,
    fitness: 0,
    totalDemerits: 0,
    previous: null,
    ratio: 0,
  };

  for (let i = 1; i <= n; i++) {
    dp[i] = {
      position: i,
      line: 0,
      fitness: 3,
      totalDemerits: INFINITE_PENALTY,
      previous: null,
      ratio: 0,
    };
  }

  for (let i = 0; i < n; i++) {
    if (dp[i].totalDemerits === INFINITE_PENALTY) continue;

    let curX = 0;
    let curStretch = 0;
    let curShrink = 0;
    let active = false;

    for (let j = i + 1; j <= n; j++) {
      const item = items[j - 1];

      if (item.type === 'box') {
        curX += item.width;
        active = true;
      } else if (item.type === 'glue') {
        if (active) {
          // Potential break point at glue, but only if not preceded by a penalty
          // In Knuth-Plass, if a glue is preceded by a penalty, the break happens at the penalty
          const prevItem = j > 1 ? items[j - 2] : null;
          if (prevItem?.type !== 'penalty') {
            const actual = curX;
            const stretch = curStretch;
            const shrink = curShrink;

            let ratio = 0;
            const delta = lineWidth - actual;
            if (delta >= 0) {
              // zero-stretch underfull: use 1e5 so demerits >= INFINITE_PENALTY → break never chosen
              ratio = stretch > 0 ? delta / stretch : delta > 0 ? 1e5 : 0;
            } else {
              ratio = shrink > 0 ? delta / shrink : delta; // delta is negative here
            }

            if (ratio >= -1) {
              // Reject strongly overfull lines
              const fitness = ratio < -0.5 ? 0 : ratio < 0.5 ? 1 : ratio < 1 ? 2 : 3;
              const badness = Math.abs(ratio);
              const demerits = (DEMERIT_PENALTY + badness)**3;

              let additionalDemerits = 0;
              const line = dp[i].line + 1;
              if (Math.abs(fitness - dp[i].fitness) > 1) {
                additionalDemerits += DEMERIT_FITNESS;
              }

              const totalDemerits = dp[i].totalDemerits + demerits + additionalDemerits;

              if (totalDemerits < dp[j].totalDemerits) {
                dp[j] = {
                  position: j,
                  line,
                  fitness,
                  totalDemerits,
                  previous: dp[i],
                  ratio,
                };
              }
            }
          }
        }

        curX += item.width;
        curStretch += item.stretch || 0;
        curShrink += item.shrink || 0;
      } else if (item.type === 'penalty') {
        // Potential break point at penalty
        const actual = curX + (item.width || 0);
        const stretch = curStretch;
        const shrink = curShrink;

        let ratio = 0;
        const delta = lineWidth - actual;
        if (delta >= 0) {
          ratio = stretch > 0 ? delta / stretch : delta > 0 ? 1e5 : 0;
        } else {
          ratio = shrink > 0 ? delta / shrink : delta;
        }

        if (ratio >= -1 || item.penalty === 1000000) {
          const fitness = ratio < -0.5 ? 0 : ratio < 0.5 ? 1 : ratio < 1 ? 2 : 3;
          let badness = Math.abs(ratio);

          // Last line doesn't care about stretch
          if (j === n && ratio > 0) badness = 0;

          const penaltyValue = item.penalty || 0;
          const demerits =
            (DEMERIT_PENALTY + badness)**3 +
            (penaltyValue >= 0 ? penaltyValue : 0)**1.5;

          let additionalDemerits = 0;
          if (item.flagged) {
            additionalDemerits += DEMERIT_FLAGGED;
            if (dp[i].previous) {
              const prevItemIndex = dp[i].position - 1;
              if (
                prevItemIndex >= 0 &&
                items[prevItemIndex].type === 'penalty' &&
                items[prevItemIndex].flagged
              ) {
                additionalDemerits += 500;
              }
            }
          }

          const line = dp[i].line + 1;
          if (Math.abs(fitness - dp[i].fitness) > 1) {
            additionalDemerits += DEMERIT_FITNESS;
          }

          const totalDemerits = dp[i].totalDemerits + demerits + additionalDemerits;

          if (totalDemerits < dp[j].totalDemerits) {
            dp[j] = {
              position: j,
              line,
              fitness,
              totalDemerits,
              previous: dp[i],
              ratio,
            };
          }
        }

        if (item.penalty === 1000000) break;
      }
    }
  }

  let cur = dp[n];
  while (cur && cur.previous) {
    breaks.unshift(cur);
    cur = cur.previous;
  }

  return breaks;
}

export function layoutParagraphKnuth(
  inlines: InlineElement[],
  params: LayoutParams,
  boldFont: PdfFont,
  italicFont: PdfFont,
  monoFont: PdfFont,
  language = 'en'
): LineBreakResult {
  const items = inlinesToItems(
    inlines,
    params.font,
    boldFont,
    italicFont,
    monoFont,
    params.page!,
    language
  );
  const breaks = breakLinesKnuth(items, params.lineWidth);
  return { breaks, items };
}

export function renderLine(
  page: PageGen,
  items: TextItem[],
  start: number,
  end: number,
  x: number,
  y: number,
  lineHeight: number
): void {
  let currentX = x;

  for (let i = start; i < end && i < items.length; i++) {
    const item = items[i];
    if (item.type === 'box' && item.font && item.brush) {
      if (item.isLatex && item.content) {
        const rect = new PdfRect(currentX, y, item.width, lineHeight);
        const fontSize = item.fontSize || item.font.size;
        page.drawLatex(item.content, rect, ETextAlign.CenterLeft, fontSize, item.brush, false);
      } else if (item.isModule && item.moduleName && item.moduleContent) {
        const rect = new PdfRect(currentX, y, item.width, lineHeight);
        const mod = moduleRegistry.get(item.moduleName);
        if (mod) {
          mod.draw(item.moduleContent, rect, ETextAlign.CenterLeft, {
            font: item.font,
            brush: item.brush,
            page,
          });
        }
      } else if (item.content) {
        const rect = new PdfRect(currentX, y, item.width, lineHeight);
        page.drawString(item.content, item.font, item.brush, null, rect, ETextAlign.CenterLeft);
      }
      currentX += item.width;
    } else if (item.type === 'glue') {
      currentX += item.width;
    } else if (item.type === 'penalty' && item.content === '-' && item.font && item.brush) {
      const rect = new PdfRect(currentX, y, item.width || 0, lineHeight);
      page.drawString('-', item.font, item.brush, null, rect, ETextAlign.CenterLeft);
      currentX += item.width || 0;
    }
  }
}
